import { z } from 'zod';
import { BILLING_INCREMENT_MINUTES } from '../constants/index.ts';
import {
  CANCELLED_SLOT_NOTICE_HOURS_MAX,
  CANCELLED_SLOT_NOTICE_HOURS_MIN,
  CANCELLED_SLOT_QUIET_HOUR_MAX,
  CANCELLED_SLOT_QUIET_HOUR_MIN,
  CANCELLED_SLOT_ROUND_HOURS_MAX,
  CANCELLED_SLOT_ROUND_HOURS_MIN,
  CANCELLED_SLOT_ROUND_SIZE_MAX,
  CANCELLED_SLOT_ROUND_SIZE_MIN,
} from '../cancelled-slots.ts';
import { LATE_CANCEL_NOTICE_HOURS_MAX, LATE_CANCEL_NOTICE_HOURS_MIN } from '../late-cancel.ts';
import { basisPointsSchema, nonNegativeCentsSchema, optionalEmail, optionalText, optionalUrl } from './common.ts';

/**
 * A location id.
 *
 * Usually a ULID, like every other id the Worker mints — but not always, and
 * the exception is in production data. Migration 0029 gave every company that
 * predates multi-location a primary location, and it minted those ids in SQL
 * as `lower(hex(randomblob(16)))`: 32 lowercase hex characters, which
 * `idSchema` rejects. Validating a location id against `idSchema` therefore
 * fails for the one location almost every established company works in —
 * saving a team member's locations, switching location, and reading a
 * location's payment settings all refused with "One or more fields are
 * invalid" while the id was perfectly real.
 *
 * That id is the foreign key in every location-scoped table, so it is
 * accepted here rather than renumbered. Anything minted since is a ULID; this
 * accepts either shape and nothing else.
 */
export const locationIdSchema = z
  .string()
  .regex(/^(?:[0-9A-HJKMNP-TV-Z]{26}|[0-9a-f]{32})$/, 'Must be a valid identifier');

export const createLocationSchema = z.object({
  name: z.string().trim().min(1, 'Enter a location name').max(120, 'Must be 120 characters or fewer'),
});

/**
 * Everything a location may override. Every override field accepts null,
 * which clears it — the location goes back to inheriting the company value.
 * Absent fields stay untouched, exactly like updateCompanySchema.
 */
export const updateLocationSchema = z
  .object({
    name: z.string().trim().min(1, 'Enter a location name').max(120).optional(),
    email: optionalEmail,
    phone: optionalText(40),
    website: optionalUrl,
    addressLine1: optionalText(160),
    addressLine2: optionalText(160),
    city: optionalText(80),
    state: optionalText(80),
    postalCode: optionalText(20),
    timezone: z.string().trim().min(1).max(64).nullable().optional(),
    defaultHourlyRateCents: nonNegativeCentsSchema
      .refine((value) => value > 0, 'Hourly rate must be greater than zero')
      .nullable()
      .optional(),
    defaultQuoteExpirationDays: z.number().int().min(1).max(365).nullable().optional(),
    taxEnabled: z.boolean().nullable().optional(),
    defaultTaxRateBasisPoints: basisPointsSchema.nullable().optional(),
    laborIsTaxable: z.boolean().nullable().optional(),
    // Whether this branch offers tips; null follows the company's answer.
    tipsEnabled: z.boolean().nullable().optional(),
    // Late cancellation fee. Null follows the company on each field.
    lateCancelFeeEnabled: z.boolean().nullable().optional(),
    lateCancelNoticeHours: z
      .number()
      .int()
      .min(LATE_CANCEL_NOTICE_HOURS_MIN)
      .max(LATE_CANCEL_NOTICE_HOURS_MAX)
      .nullable()
      .optional(),
    lateCancelFeeCents: nonNegativeCentsSchema.nullable().optional(),
    // Filling a canceled spot. Null follows the company on each field.
    autoOfferCancelledSlots: z.boolean().nullable().optional(),
    cancelledSlotNoticeHours: z
      .number()
      .int()
      .min(CANCELLED_SLOT_NOTICE_HOURS_MIN)
      .max(CANCELLED_SLOT_NOTICE_HOURS_MAX)
      .nullable()
      .optional(),
    cancelledSlotRoundHours: z
      .number()
      .int()
      .min(CANCELLED_SLOT_ROUND_HOURS_MIN)
      .max(CANCELLED_SLOT_ROUND_HOURS_MAX)
      .nullable()
      .optional(),
    cancelledSlotRoundSize: z
      .number()
      .int()
      .min(CANCELLED_SLOT_ROUND_SIZE_MIN)
      .max(CANCELLED_SLOT_ROUND_SIZE_MAX)
      .nullable()
      .optional(),
    cancelledSlotQuietStartHour: z
      .number()
      .int()
      .min(CANCELLED_SLOT_QUIET_HOUR_MIN)
      .max(CANCELLED_SLOT_QUIET_HOUR_MAX)
      .nullable()
      .optional(),
    cancelledSlotQuietEndHour: z
      .number()
      .int()
      .min(CANCELLED_SLOT_QUIET_HOUR_MIN)
      .max(CANCELLED_SLOT_QUIET_HOUR_MAX)
      .nullable()
      .optional(),
    cancelledSlotRequireApproval: z.boolean().nullable().optional(),
    billingIncrementMinutes: z
      .number()
      .int()
      .refine((value) => BILLING_INCREMENT_MINUTES.includes(value), 'Not a supported billing increment')
      .nullable()
      .optional(),
    quoteNumberPrefix: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{1,10}$/, 'Use up to 10 letters, numbers or hyphens')
      .nullable()
      .optional(),
    invoiceNumberPrefix: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{1,10}$/, 'Use up to 10 letters, numbers or hyphens')
      .nullable()
      .optional(),
    defaultTerms: optionalText(20_000),
    appointmentConfirmLeadHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 14)
      .nullable()
      .optional(),
    appointmentReminderLeadHours: z
      .number()
      .int()
      .min(1)
      .max(24 * 14)
      .nullable()
      .optional(),
    smsSenderLabel: optionalText(40),
    // This branch's own currency (0114); null follows the company's. Checked
    // against the chargeable list in the route, like the company's, so a code
    // already stored stays saveable after it leaves the list.
    currencyCode: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{3}$/, 'Use a three-letter currency code')
      .transform((code) => code.toUpperCase())
      .nullable()
      .optional(),
    // Working hours (0060). Same shapes as the company's, plus null — which
    // is how a branch stops keeping its own hours and follows head office.
    workDayStartLocal: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use the format HH:MM (24-hour)')
      .nullable()
      .optional(),
    workDayEndLocal: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use the format HH:MM (24-hour)')
      .nullable()
      .optional(),
    // Accepts the array a form sends AND the comma string an export carries,
    // for the same round-trip reason as the company's.
    workDays: z
      .union([
        z.array(z.number().int().min(0).max(6)),
        z
          .string()
          .regex(/^[0-6](,[0-6])*$/, 'Use weekday numbers 0-6, comma separated')
          .transform((raw) => raw.split(',').map(Number)),
      ])
      .pipe(
        z
          .array(z.number().int().min(0).max(6))
          .min(1, 'Keep at least one working day')
          .max(7)
          .transform((days) => [...new Set(days)].sort((a, b) => a - b).join(',')),
      )
      .nullable()
      .optional(),
  })
  .strict();

export const switchLocationSchema = z.object({
  locationId: locationIdSchema,
});

export type CreateLocationInput = z.infer<typeof createLocationSchema>;
export type UpdateLocationInput = z.infer<typeof updateLocationSchema>;
